import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ManualApprovalReason, SortDirection } from '@mesa-labs/mesa-api/dist/types';
import { MicroTable, utils } from '@mesa-labs/mesa-ui';

import { Header } from '../../common/Header';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import InvoiceLoadsByDay from './InvoiceLoadsByDay';
import DynamicStats, { DynamicStatsChartSelection } from './DynamicStatsColumnChart';
import { useGetAllExternalClientsAsFacilitatorQuery, useGetAllExternalVendorsAsFacilitatorQuery, useGetAllInvoiceApprovalsAsFacilitatorQuery } from '../../../redux/api/facilitators';
import { useSelector } from '../../../redux/hooks';
import { DashboardEvents, trackEvent } from '../../../analytics';

const DashboardPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: ${(props) => props.theme.colors.White};
`;

const CustomHeader = styled(Header)`
  padding-bottom: 32px;  
`;

const Card = styled.div<{ marginBottom?: number }>`
  background: #F6F8FA;
  border-radius: 4px;
  flex: 1;
  margin-bottom: ${(props) => props.marginBottom || 32}px;
  padding: 24px;  
`;

const CardTitle = styled.h3<{ marginBottom?: number }>`
  color: #181D27;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  margin-bottom: ${(props) => props.marginBottom || 0}px;
`;

const ShowMoreLink = styled(Link)`
  color: #5A05FF;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;

  &:hover {
    text-decoration: underline;
  }
`;

const MetricCards = styled.div<{ marginBottom?: number }>`
  display: flex;  
  gap: 16px; 
  margin-bottom: ${(props) => props.marginBottom || 0}px;
  width: 100%;
`;

const MetricCard = styled(motion.div) <{ active: boolean }>`
  border-radius: 4px;
  background: #FFF;
  cursor: pointer;
  display: flex;
  flex-basis: 33.33%;
  flex-direction: column;
  flex-grow: 1;
  gap: 10px;
  padding: 24px 20px;
  transition: box-shadow 300ms ease-in-out, background 300ms ease-in-out;

  &:hover {
    box-shadow: 0px 4px 6px -1px rgba(10, 13, 18, 0.10), 0px 2px 4px -2px rgba(10, 13, 18, 0.06);  
  }

  ${(props) => props.active && `
    border: 2px solid #743BFF;
    background: #F2F0FF;
    box-shadow: 0px 4px 6px -1px rgba(10, 13, 18, 0.10), 0px 2px 4px -2px rgba(10, 13, 18, 0.06);  
  `}
`;

const MetricCardTitleContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const MetricCardTitle = styled.h4`
  color: #686C8B;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`;

const MetricCardValue = styled.p`
  color: #0A0A0A;
  font-family: ${(props) => props.theme.fonts.grotesk};
  font-size: 32px;
  font-weight: 500;
  line-height: 120%;
`;

const MicroTables = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;

  & > div {
    flex-basis: 50%;
  }
`;

const CardContainer = styled.div`
  display: flex;
  gap: 24px;
  justify-content: space-between;
  width: 100%;
`;

const MetricCardTitleLine = ({ color }: { color: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="4" height="14" viewBox="0 0 4 14" fill="none">
    <path d="M2 1.5L2 12.5" stroke={color} strokeWidth="3" strokeLinecap="round" />
  </svg>
);

function DashboardPage(): React.ReactElement {
  const [activeMetricCard, setActiveMetricCard] = useState<DynamicStatsChartSelection>('vendorsFunded');
  const partnerId = useSelector((state) => state.auth.partnerId);
  const accessToken = useSelector((state) => state.auth.accessToken);

  const externalClientsFilter = useMemo(() => ({
    sortField: 'totalInvoiceSpendT12m',
    sortDirection: SortDirection.DESCENDING,
    limit: 9,
  }), []);

  const {
    data: {
      data: clients = [],
    } = {},
    isLoading: isGetAllExternalClientsAsFacilitatorLoading,
    isFetching: isGetAllExternalClientsAsFacilitatorFetching,
  } = useGetAllExternalClientsAsFacilitatorQuery({
    accessToken: accessToken!,
    partnerId: partnerId!,
    ...externalClientsFilter,
  }, {
    skip: !partnerId,
  });

  const externalVendorsFilter = useMemo(() => ({
    sortField: 'totalInvoiceSpend',
    sortDirection: SortDirection.DESCENDING,
    limit: 9,
  }), []);

  const {
    data: {
      data: vendors = [],
    } = {},
    isLoading: isGetAllExternalVendorsAsFacilitatorLoading,
    isFetching: isGetAllExternalVendorsAsFacilitatorFetching,
  } = useGetAllExternalVendorsAsFacilitatorQuery({ accessToken: accessToken!, partnerId: partnerId!, ...externalVendorsFilter }, { skip: !partnerId });

  const invoiceApprovalsFilter = useMemo(() => ({
    approvalReasons: [
      ManualApprovalReason.INVALID_INVOICE_DATE,
      ManualApprovalReason.INVALID_DUE_DATE,
      ManualApprovalReason.AMOUNT_EXCEEDS_THRESHOLD,
    ],
    sortDirection: SortDirection.DESCENDING,
    limit: 5,
  }), []);

  useEffect(() => {
    trackEvent(DashboardEvents.SourcingDashboardView);
  }, []);

  return (
    <DashboardPageContainer>
      <CustomHeader>Dashboard</CustomHeader>

      <Card>
        <MetricCards marginBottom={20}>
          <MetricCard
            active={activeMetricCard === 'vendorsFunded'}
            onClick={() => setActiveMetricCard('vendorsFunded')}
            layout
          >
            <MetricCardTitleContainer>
              <MetricCardTitleLine color="#335CFF" />
              <MetricCardTitle>Suppliers Funded</MetricCardTitle>
            </MetricCardTitleContainer>
            <MetricCardValue>1,834</MetricCardValue>
          </MetricCard>

          <MetricCard
            active={activeMetricCard === 'totalAmountFunded'}
            onClick={() => setActiveMetricCard('totalAmountFunded')}
            layout
          >
            <MetricCardTitleContainer>
              <MetricCardTitleLine color="#12B76A" />
              <MetricCardTitle>Total Funded Amount</MetricCardTitle>
            </MetricCardTitleContainer>
            <MetricCardValue>$448,124,983</MetricCardValue>
          </MetricCard>

          <MetricCard
            active={activeMetricCard === 'averageDaysAccelerated'}
            onClick={() => setActiveMetricCard('averageDaysAccelerated')}
            layout
          >
            <MetricCardTitleContainer>
              <MetricCardTitleLine color="#FDB71C" />
              <MetricCardTitle>Average Days Accelerated</MetricCardTitle>
            </MetricCardTitleContainer>
            <MetricCardValue>52.6</MetricCardValue>
          </MetricCard>
        </MetricCards>

        <DynamicStats selectedChart={activeMetricCard} />
      </Card>

      <Card>
        <CardTitle marginBottom={20}>Top Clients and Suppliers</CardTitle>
        <MicroTables>
          <MicroTable
            title="Top Clients"
            showMoreLink="/clients"
            colorIndicator="#743BFF"
            rows={clients.map((client) => ({
              left: client.externalClientName || '',
              right: utils.formatCurrency(Math.round(client.totalInvoiceSpendT12m), 'USD').replace(/\.00$/, ''),
              link: `/clients/${client.externalClientId}`,
            }))}
            isLoading={isGetAllExternalClientsAsFacilitatorLoading || isGetAllExternalClientsAsFacilitatorFetching}
          />
          <MicroTable
            title="Top Suppliers"
            showMoreLink="/suppliers"
            colorIndicator="#17A34A"
            rows={vendors.map((vendor) => ({
              left: vendor.externalVendorName || '',
              right: utils.formatCurrency(Math.round(vendor.totalInvoiceSpendT12m), 'USD').replace(/\.00$/, ''),
              link: `/suppliers/${vendor.externalVendorId}`,
            }))}
            isLoading={isGetAllExternalVendorsAsFacilitatorLoading || isGetAllExternalVendorsAsFacilitatorFetching}
          />
        </MicroTables>
      </Card>

      <CardContainer>
        <Card>
          <CardTitle marginBottom={20}>Invoice Loads by Day</CardTitle>
          <InvoiceLoadsByDay />
        </Card>
      </CardContainer>
    </DashboardPageContainer>
  );
}

export default DashboardPage;
